// WebGPU shaders for various image transition effects

export const TRANSITION_SHADERS = {
  crossfade: `
    @group(0) @binding(0) var<storage, read> image1: array<vec4<f32>>;
    @group(0) @binding(1) var<storage, read> image2: array<vec4<f32>>;
    @group(0) @binding(2) var<storage, read_write> output: array<vec4<f32>>;
    @group(0) @binding(3) var<uniform> params: TransitionParams;
    
    struct TransitionParams {
      progress: f32,
      width: u32,
      height: u32,
      time: f32
    }
    
    @compute @workgroup_size(8, 8)
    fn main(@builtin(global_invocation_id) id: vec3<u32>) {
      let index = id.y * params.width + id.x;
      if (id.x >= params.width || id.y >= params.height) { return; }
      
      let smoothProgress = smoothstep(0.0, 1.0, params.progress);
      output[index] = mix(image1[index], image2[index], smoothProgress);
    }
  `,

  slide: `
    @group(0) @binding(0) var<storage, read> image1: array<vec4<f32>>;
    @group(0) @binding(1) var<storage, read> image2: array<vec4<f32>>;
    @group(0) @binding(2) var<storage, read_write> output: array<vec4<f32>>;
    @group(0) @binding(3) var<uniform> params: TransitionParams;
    
    struct TransitionParams {
      progress: f32,
      width: u32,
      height: u32,
      time: f32
    }
    
    @compute @workgroup_size(8, 8)
    fn main(@builtin(global_invocation_id) id: vec3<u32>) {
      let index = id.y * params.width + id.x;
      if (id.x >= params.width || id.y >= params.height) { return; }
      
      let slideOffset = i32(f32(params.width) * params.progress);
      
      if (i32(id.x) < slideOffset) {
        output[index] = image2[index];
      } else {
        output[index] = image1[index];
      }
    }
  `,

  wipe: `
    @group(0) @binding(0) var<storage, read> image1: array<vec4<f32>>;
    @group(0) @binding(1) var<storage, read> image2: array<vec4<f32>>;
    @group(0) @binding(2) var<storage, read_write> output: array<vec4<f32>>;
    @group(0) @binding(3) var<uniform> params: TransitionParams;
    
    struct TransitionParams {
      progress: f32,
      width: u32,
      height: u32,
      time: f32
    }
    
    @compute @workgroup_size(8, 8)
    fn main(@builtin(global_invocation_id) id: vec3<u32>) {
      let index = id.y * params.width + id.x;
      if (id.x >= params.width || id.y >= params.height) { return; }
      
      let wipePos = params.progress * f32(params.height);
      let feather = 10.0; // Soft edge
      
      let distance = f32(id.y) - wipePos;
      let alpha = 1.0 - smoothstep(-feather, feather, distance);
      
      output[index] = mix(image1[index], image2[index], alpha);
    }
  `,

  zoom: `
    @group(0) @binding(0) var<storage, read> image1: array<vec4<f32>>;
    @group(0) @binding(1) var<storage, read> image2: array<vec4<f32>>;
    @group(0) @binding(2) var<storage, read_write> output: array<vec4<f32>>;
    @group(0) @binding(3) var<uniform> params: TransitionParams;
    
    struct TransitionParams {
      progress: f32,
      width: u32,
      height: u32,
      time: f32
    }
    
    @compute @workgroup_size(8, 8)
    fn main(@builtin(global_invocation_id) id: vec3<u32>) {
      let index = id.y * params.width + id.x;
      if (id.x >= params.width || id.y >= params.height) { return; }
      
      let center = vec2<f32>(f32(params.width) * 0.5, f32(params.height) * 0.5);
      let pos = vec2<f32>(f32(id.x), f32(id.y));
      let dist = distance(pos, center) / (f32(params.width) * 0.5);
      
      let zoomFactor = 1.0 + params.progress * 2.0;
      let zoomedPos = center + (pos - center) / zoomFactor;
      
      let zoomAlpha = smoothstep(0.3, 0.7, params.progress);
      
      // Simple sampling (in real implementation, would use proper texture sampling)
      if (zoomedPos.x >= 0.0 && zoomedPos.x < f32(params.width) && 
          zoomedPos.y >= 0.0 && zoomedPos.y < f32(params.height)) {
        let zoomIndex = u32(zoomedPos.y) * params.width + u32(zoomedPos.x);
        output[index] = mix(image1[zoomIndex], image2[index], zoomAlpha);
      } else {
        output[index] = image2[index];
      }
    }
  `,

  pixelate: `
    @group(0) @binding(0) var<storage, read> image1: array<vec4<f32>>;
    @group(0) @binding(1) var<storage, read> image2: array<vec4<f32>>;
    @group(0) @binding(2) var<storage, read_write> output: array<vec4<f32>>;
    @group(0) @binding(3) var<uniform> params: TransitionParams;
    
    struct TransitionParams {
      progress: f32,
      width: u32,
      height: u32,
      time: f32
    }
    
    @compute @workgroup_size(8, 8)
    fn main(@builtin(global_invocation_id) id: vec3<u32>) {
      let index = id.y * params.width + id.x;
      if (id.x >= params.width || id.y >= params.height) { return; }
      
      let maxPixelSize = 20.0;
      let pixelSize = max(1.0, maxPixelSize * params.progress * (1.0 - params.progress) * 4.0);
      
      let pixelX = f32(u32(f32(id.x) / pixelSize) * u32(pixelSize));
      let pixelY = f32(u32(f32(id.y) / pixelSize) * u32(pixelSize));
      let pixelIndex = u32(pixelY) * params.width + u32(pixelX);
      
      let mixProgress = smoothstep(0.0, 1.0, params.progress);
      
      if (pixelIndex < arrayLength(&image1)) {
        output[index] = mix(image1[pixelIndex], image2[pixelIndex], mixProgress);
      } else {
        output[index] = mix(image1[index], image2[index], mixProgress);
      }
    }
  `,

  ripple: `
    @group(0) @binding(0) var<storage, read> image1: array<vec4<f32>>;
    @group(0) @binding(1) var<storage, read> image2: array<vec4<f32>>;
    @group(0) @binding(2) var<storage, read_write> output: array<vec4<f32>>;
    @group(0) @binding(3) var<uniform> params: TransitionParams;
    
    struct TransitionParams {
      progress: f32,
      width: u32,
      height: u32,
      time: f32
    }
    
    @compute @workgroup_size(8, 8)
    fn main(@builtin(global_invocation_id) id: vec3<u32>) {
      let index = id.y * params.width + id.x;
      if (id.x >= params.width || id.y >= params.height) { return; }
      
      let center = vec2<f32>(f32(params.width) * 0.5, f32(params.height) * 0.5);
      let pos = vec2<f32>(f32(id.x), f32(id.y));
      let dist = distance(pos, center);
      
      let rippleFreq = 8.0;
      let rippleAmp = 30.0;
      let rippleSpeed = 300.0;
      
      let wave = sin(dist * rippleFreq - params.progress * rippleSpeed) * rippleAmp;
      let rippleDist = dist + wave * params.progress * (1.0 - params.progress) * 4.0;
      
      let rippleAlpha = smoothstep(rippleDist - 50.0, rippleDist + 50.0, 
                                   params.progress * f32(max(params.width, params.height)));
      
      output[index] = mix(image1[index], image2[index], rippleAlpha);
    }
  `,

  glitch: `
    @group(0) @binding(0) var<storage, read> image1: array<vec4<f32>>;
    @group(0) @binding(1) var<storage, read> image2: array<vec4<f32>>;
    @group(0) @binding(2) var<storage, read_write> output: array<vec4<f32>>;
    @group(0) @binding(3) var<uniform> params: TransitionParams;
    
    struct TransitionParams {
      progress: f32,
      width: u32,
      height: u32,
      time: f32
    }
    
    fn random(seed: f32) -> f32 {
      return fract(sin(seed * 12.9898) * 43758.5453);
    }
    
    @compute @workgroup_size(8, 8)
    fn main(@builtin(global_invocation_id) id: vec3<u32>) {
      let index = id.y * params.width + id.x;
      if (id.x >= params.width || id.y >= params.height) { return; }
      
      let glitchIntensity = params.progress * (1.0 - params.progress) * 4.0;
      let noise = random(f32(id.y) * 0.1 + params.time * 10.0);
      
      var color1 = image1[index];
      var color2 = image2[index];
      
      // RGB shift effect
      if (noise < glitchIntensity) {
        let shift = i32(glitchIntensity * 10.0);
        let shiftedIndex1 = clamp(i32(index) + shift, 0, i32(arrayLength(&image1)) - 1);
        let shiftedIndex2 = clamp(i32(index) - shift, 0, i32(arrayLength(&image1)) - 1);
        
        color1.r = image1[shiftedIndex1].r;
        color1.b = image1[shiftedIndex2].b;
        color2.r = image2[shiftedIndex1].r;
        color2.b = image2[shiftedIndex2].b;
      }
      
      let mixProgress = smoothstep(0.0, 1.0, params.progress);
      output[index] = mix(color1, color2, mixProgress);
    }
  `,

  burn: `
    @group(0) @binding(0) var<storage, read> image1: array<vec4<f32>>;
    @group(0) @binding(1) var<storage, read> image2: array<vec4<f32>>;
    @group(0) @binding(2) var<storage, read_write> output: array<vec4<f32>>;
    @group(0) @binding(3) var<uniform> params: TransitionParams;
    
    struct TransitionParams {
      progress: f32,
      width: u32,
      height: u32,
      time: f32
    }
    
    fn noise(pos: vec2<f32>) -> f32 {
      return fract(sin(dot(pos, vec2<f32>(12.9898, 78.233))) * 43758.5453);
    }
    
    @compute @workgroup_size(8, 8)
    fn main(@builtin(global_invocation_id) id: vec3<u32>) {
      let index = id.y * params.width + id.x;
      if (id.x >= params.width || id.y >= params.height) { return; }
      
      let pos = vec2<f32>(f32(id.x), f32(id.y));
      let burnNoise = noise(pos * 0.01) * 0.5 + noise(pos * 0.02) * 0.3 + noise(pos * 0.05) * 0.2;
      let burnProgress = params.progress + (burnNoise - 0.5) * 0.3;
      
      let burnEdge = smoothstep(burnProgress - 0.1, burnProgress, burnNoise);
      let fireColor = vec4<f32>(1.0, 0.4, 0.0, 1.0); // Orange fire color
      
      var result: vec4<f32>;
      if (burnProgress > burnNoise) {
        result = image2[index];
      } else if (burnProgress > burnNoise - 0.05) {
        result = mix(image1[index], fireColor, burnEdge);
      } else {
        result = image1[index];
      }
      
      output[index] = result;
    }
  `
};

// Utility function to get transition shader based on beat intensity
export function getTransitionForBeat(beatIntensity, previousTransition = 'crossfade') {
  const transitionTypes = Object.keys(TRANSITION_SHADERS);
  
  if (beatIntensity < 0.3) {
    // Low intensity - gentle transitions
    return Math.random() < 0.7 ? 'crossfade' : 'wipe';
  } else if (beatIntensity < 0.6) {
    // Medium intensity - more dynamic
    const mediumTransitions = ['slide', 'zoom', 'pixelate', 'crossfade'];
    return mediumTransitions[Math.floor(Math.random() * mediumTransitions.length)];
  } else {
    // High intensity - dramatic effects
    const dramaticTransitions = ['ripple', 'glitch', 'burn', 'zoom'];
    return dramaticTransitions[Math.floor(Math.random() * dramaticTransitions.length)];
  }
}

// Parameters structure for transitions
export function createTransitionParams(progress, width, height, time = 0) {
  return new Float32Array([progress, width, height, time]);
}